import mixpanel from 'mixpanel-browser';
import { datadogLogs } from '@datadog/browser-logs';
import type { MixpanelEventMap } from './mixpanelEvents';

const IDENTIFY_MERGE_EVENT: keyof MixpanelEventMap = 'User Identified';

// Set by the Auth0 redirect callback so a real sign-in can be told apart from a reload.
let isLoginPending = false;

export function markLoginPending(): void {
  isLoginPending = true;
}

export function consumePendingLogin(): boolean {
  const wasPending = isLoginPending;
  isLoginPending = false;
  return wasPending;
}

function logMixpanelError(
  action: string,
  contextData: Record<string, unknown>,
  error: unknown
): void {
  datadogLogs.logger.warn(`[mixpanel] ${action} failed`, contextData, error as Error);
}

export const mixpanelClient = {
  track(eventName: keyof MixpanelEventMap, properties?: Record<string, unknown>): void {
    try {
      mixpanel.track(eventName, properties);
    } catch (error) {
      logMixpanelError('track', { eventName }, error);
    }
  },

  identify(uniqueId: string): void {
    try {
      mixpanel.identify(uniqueId);
    } catch (error) {
      logMixpanelError('identify', { uniqueId }, error);
    }
  },

  /**
   * Identify the user and fire the merge event in one call — same pairing site uses
   * so Mixpanel can merge the pre-login anonymous ID into the identified profile.
   */
  identifyWithMergeEvent(uniqueId: string): void {
    try {
      mixpanel.identify(uniqueId);
      mixpanel.track(IDENTIFY_MERGE_EVENT, { user_id: uniqueId });
    } catch (error) {
      logMixpanelError('identifyWithMergeEvent', { uniqueId }, error);
    }
  },

  register(data: Record<string, string | number | boolean | null>): void {
    try {
      mixpanel.register(data);
    } catch (error) {
      logMixpanelError('register', { keys: Object.keys(data) }, error);
    }
  },

  reset(): void {
    try {
      mixpanel.reset();
    } catch (error) {
      logMixpanelError('reset', {}, error);
    }
  },
};
